import { Injectable } from '@angular/core';
import moment from 'moment/moment';
import {PrimengTableCols} from '@sharedcommon/interfaces/primeng-table-cols';
import {PrimengTableVariableRequirementModel} from '@sharedcommon/model/primeng-table-variable-requirement.model';
import {VariableDto} from '@sharedrest/equation.restservice';
import {TranslateService} from '@ngx-translate/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {PrimengTableEditModel} from '@sharedcommon/model/primeng-table-edit.model';
import {PrimengCheckColumnModel} from '@sharedcommon/model/primeng-check-column.model';
import {PrimengDropdownOptionInterface} from '@sharedcommon/interfaces/primeng-dropdown-option.interface';
import { ActionTableEvent } from '@sharedcommon/model/action-table-event.model';

@Injectable({
  providedIn: 'root'
})
export class PrimengTableService {

  static TEXT_CONDITION = 'label.admin.variable.type.condition';
  static TEXT_EQUATION = 'label.admin.variable.type.equation';
  static TEXT_VARIABLE_TYPE = 'label.common.type';
  static TEXT_VARIABLE_DEFAULT_VALUE = 'label.common.default-value';
  static TEXT_VARIABLE_MIN = 'label.common.min-value';
  static TEXT_VARIABLE_MAX = 'label.common.max-value';
  static SEMI_AUTO_TABLE_SESSION_ID = 'semiAutoImportTable';

  /**
   * get the key used to store the table informations in the local storage
   * @param sessionId the session ID of the table
   * @returns the key used in the localstorage to store the table informations
   */
  static getTableStateKey(sessionId: string): string {
    return 'neq-table-session-' + sessionId ;
  }

  /**
   * Reset the table state for a given session ID
   * @param sessionId the Session ID of the table
   */
  static resetTableState(sessionId: string): void {
    localStorage.removeItem(this.getTableStateKey(sessionId));
  }

  /**
   * Reset the table state for the semi auto import tables
   */
  static resetSemiAutoImportTableState(): void {
    this.resetTableState(this.returnSemiAutoTableSessionId());
  }

  /**
   * Return the session ID to use for semi auto table session
   * @returns the corresponding session ID fot semi auto table
   */
  static returnSemiAutoTableSessionId(): string {
    return this.SEMI_AUTO_TABLE_SESSION_ID;
  }


  /**
   * convert timestamp to date string
   *
   * @param timestamp date timestamp
   * @param dateFormat date format
   */
  static showDateString(timestamp: number, dateFormat = 'MM/DD/YYYY'): string {
    const frontTimestamp = 1000;
    return moment.unix(timestamp / frontTimestamp).format(dateFormat);
  }

  /**
   * Assigne translation label key to the right header
   *
   * @param field name of the field
   * @param label translation key
   * @param cols list of the current table's cols
   */
  static assignLabel(field: string, label: string, cols: PrimengTableCols[]) {
    cols.find((col: PrimengTableCols) => col.field === field).header = label;
  }

  /**
   * Assigne or remove header to col and col to col list
   *
   * @param header translation key
   * @param cols list of the current table's cols
   * @param field field id of the col
   * @param type Add or remove the col
   */
  static assignLabelAndCol( header: string, cols: PrimengTableCols[], field: string, type: 'add' | 'remove') {
    if (type === 'add') {
      if (cols.find((currentCol: PrimengTableCols) => (currentCol.field === field))) {
        cols.find((currentCol: PrimengTableCols) => (currentCol.field === field)).header = header;
        cols.find((currentCol: PrimengTableCols) => (currentCol.field === field)).show = true;
      }
    } else {
      cols.find((currentCol: PrimengTableCols) => (currentCol.field === field)).header = '';
      cols.find((currentCol: PrimengTableCols) => (currentCol.field === field)).show = false;
    }
  }

  /**
   * Show or hide a column from a column list
   *
   * @param cols List of Table columns
   * @param field Identifier of the col
   * @param show True = show col, false = hide col
   */
  static changeColShow(cols: PrimengTableCols[], field: string, show: boolean) {
    const columnToTreat = cols.find((col: PrimengTableCols) => col.field === field );
    if (columnToTreat?.show !== undefined) {
      columnToTreat.show = show;
    }
  }

  /**
   * Remove item from array depending of iteratee
   *
   * @param array array of item to be deleted
   * @param iteratee deletion condition
   */
  static remove(array: Array<any>, iteratee: (value: any, index: number) => any) {
    const deletedItems = [];
    if (array) {
      let index = array.length;
      while (index--) {
        const item = array[index];
        if (iteratee(item, index)) {
          array.splice(index, 1);
          deletedItems.push(item);
        }
      }
    }
    return deletedItems;
  }

  /**
   * check if all the dynamic requirement value are initialized
   *
   * @param dynamicValue
   */
  static haveAllDynamicValue(dynamicValue: PrimengTableVariableRequirementModel): boolean {
    return (dynamicValue?.vvCache !== undefined &&
      dynamicValue?.translateService !== undefined &&
      dynamicValue?.clientService !== undefined);
  }

  /**
   * Return the right toolitip translated string from variables
   *
   * @param variable variable of the dynamic column
   * @param translateService Translation service
   */
  static returnVariableTooltip(variable: VariableDto, translateService: TranslateService) {
    let tooltip = '';
    tooltip += `• ${
      translateService.instant(PrimengTableService.TEXT_VARIABLE_TYPE)
    } : ${
      variable.conditionVariable ?
        translateService.instant(PrimengTableService.TEXT_CONDITION) :
        translateService.instant(PrimengTableService.TEXT_EQUATION)
    }\n`;
    tooltip += `• ${
      translateService.instant(PrimengTableService.TEXT_VARIABLE_DEFAULT_VALUE)
    } : ${variable.defaultValue}\n`;
    if (!variable.conditionVariable) {
      tooltip += `• ${
        translateService.instant(PrimengTableService.TEXT_VARIABLE_MIN)
      } : ${variable.min}\n`;
      tooltip += `• ${
        translateService.instant(PrimengTableService.TEXT_VARIABLE_MAX)
      } : ${variable.max}\n`;
    }
    return tooltip;
  }

  /**
   * Add options to the filter option list of the designated column
   *
   * @param cols
   * @param field
   * @param options
   */
  static updateColFilterOptions(cols: PrimengTableCols[], field: string, options: PrimengDropdownOptionInterface[]): void {
    const chooseCol = cols.find((col: PrimengTableCols) => col.field === field);
    if (chooseCol.filterOptions !== undefined) {
      chooseCol.filterOptions.push(...options);
    }
  }

  /**
   * Subject and Observable to broadcast the action on table
   */
  actionEvent$: Observable<ActionTableEvent>;
  actionEvent = new BehaviorSubject<ActionTableEvent>(null);

  /**
   * Subject and Observable to broadcast the selection of a row
   */
  selectRowEvent$: Observable<any[]>;
  selectRowEvent = new BehaviorSubject<any[]>(null);

  /**
   * Subject and Observable to broadcast the edition of the table cell
   */
  editCellEvent$: Observable<PrimengTableEditModel>;
  editCellEvent = new BehaviorSubject<PrimengTableEditModel>(null);

  /**
   * Subject and Observable to broadcast the check of the checkbox on a cell
   */
  checkCellEvent$: Observable<PrimengCheckColumnModel>;
  checkCellEvent = new BehaviorSubject<PrimengCheckColumnModel>(null);

  /**
   * Subject and Observable to broadcast the check of all the cell for a column
   */
  checkAllCellEvent$: Observable<PrimengCheckColumnModel>;
  checkAllCellEvent = new BehaviorSubject<PrimengCheckColumnModel>(null);

  /**
   * Subject and Observable to broadcast the delete of table column
   */
  deleteColumnEvent$: Observable<any>;
  deleteColumnEvent = new BehaviorSubject<any>(null);

  /**
   * Subject and Observable to broadcast Clear state and reste of the table
   */
  resteTableEvent$: Observable<string>;
  resteTableEvent = new BehaviorSubject<string>(null);

  /**
   * Subject and Observable to broadcast the current page of the table
   */
  currentPageEvent$: Observable<any[]>;
  currentPageEvent = new BehaviorSubject<any[]>(null);

  /**
   * Subject and Observable to broadcast the custom edition of the table cell
   */
  changeDataEvent$: Observable<any>;
  changeDataEvent = new BehaviorSubject<any>(null);

  referenceId: string;

  isDisabled: boolean;

  constructor() {
    this.actionEvent$ = this.actionEvent.asObservable();
    this.selectRowEvent$ = this.selectRowEvent.asObservable();
    this.editCellEvent$ = this.editCellEvent.asObservable();
    this.checkCellEvent$ = this.checkCellEvent.asObservable();
    this.checkAllCellEvent$ = this.checkAllCellEvent.asObservable();
    this.deleteColumnEvent$ = this.deleteColumnEvent.asObservable();
    this.resteTableEvent$ = this.resteTableEvent.asObservable();
    this.currentPageEvent$ = this.currentPageEvent.asObservable();
    this.changeDataEvent$ = this.changeDataEvent.asObservable();
  }

  /**
   * Broadcast the action event to all subscribed actionEvent$ observable
   *
   * @param value
   */
  setActionEvent(value: ActionTableEvent) {
    this.actionEvent.next(value);
  }

  /**
   * Broadcast the selection of row event to all subscribed selectRowEvent$ observable
   *
   * @param value
   */
  setSelectionRowEvent(value: any[]) {
    this.selectRowEvent.next(value);
  }

  /**
   * Broadcast the edition of the table cell event to all subscribed selectRowEvent$ observable
   *
   * @param value
   */
  setEditCellEvent(value: PrimengTableEditModel) {
    this.editCellEvent.next(value);
  }

  /**
   * Broadcast the check of the checkbox on a cell event to all subscribed selectRowEvent$ observable
   *
   * @param value
   */
  setCheckCellEvent(value: PrimengCheckColumnModel) {
    this.checkCellEvent.next(value);
  }

  /**
   * Broadcast the check of all the cell for a column event to all subscribed selectRowEvent$ observable
   *
   * @param value
   */
  setCheckAllCellEvent(value: PrimengCheckColumnModel) {
    this.checkAllCellEvent.next(value);
  }

  /**
   * Broadcast the delete of table column event to all subscribed selectRowEvent$ observable
   *
   * @param value
   */
  setDeleteColumnEvent(value: any) {
    this.deleteColumnEvent.next(value);
  }

  /**
   * Broadcast the Clear state and reset of the table event to all subscribed selectRowEvent$ observable
   *
   * @param value
   */
  setResetTableEvent(value: string) {
    this.resteTableEvent.next(value);
  }

  /**
   * Broadcast the current page event to all subscribed currentPageEvent$ observable
   *
   * @param value
   */
  setCurrentPageEvent(value: any[]) {
    this.currentPageEvent.next(value);
  }

  /**
   * Broadcast a custom event
   *
   * @param value
   */
  setChangeDataEvent(value: any) {
    this.changeDataEvent.next(value);
  }

  /**
   * Return the raw data of a row for a given col
   * @param col     the col where the data is from
   * @param rowData the row data
   * @returns the raw data for the given row and col
   */
  static returnRawRowDataToDisplay(col: PrimengTableCols, rowData: any) {
    return (!col.fieldLvl2) ? rowData[col.field] : rowData[col.field]?.[col.fieldLvl2]
  }

  /**
   * Return the filter column configuration if the filter type is forced on the column
   * @param col       the col to filter
   * @param confType  the type of the conf to get
   * @return the filter conf according the col.filterType
  */
 private static returnFilterConfByFilterType(col: PrimengTableCols, confType: 'type' | 'matchMode'): string {
   if (col?.filterType) {
     if (col.filterType === 'boolean') {
       return (confType === 'type') ? 'boolean' : null;
     }
     if (col.filterType === 'text') {
       return (confType === 'type') ? 'text' : 'contains';
     }
   }
   return null;

 }

  /**
   * Return the right conf for the filter
   * @param col       the col to filter
   * @param confType  the type of the conf to get
   */
  static returnFilterConf(col: PrimengTableCols, confType: 'type' | 'matchMode'): string {
    const filterConfAccordingFilterType = this.returnFilterConfByFilterType(col, confType);
    if (filterConfAccordingFilterType) {
      return filterConfAccordingFilterType;
    }
    if (col?.type) {
      if (
        col.type.toLowerCase().includes('check')
        || col.type === 'variableType'
        || col.type === 'translationChoose'
      ) {
        return (confType === 'type') ? 'boolean' : null;
      }
    }
    return (confType === 'type') ? 'text' : 'contains';
  }
}
